import React, { useState, useEffect } from 'react';
import { isSuperAdmin, isAdminAuthorized } from '../utils/userUtils';
import axios from 'axios';
import { API_BASE_URL } from '../config/ApiConfig';
import { useNavigate } from 'react-router-dom';
import Layout from './components/Layout';
import TableContainer from './components/TableContainer';
import TableHeader from './components/TableHeader';
import DefaultModal from './components/modals/DefaultModal';
import Toolbar from './components/Toolbar';
import PageHeader from './components/PageHeader';
import PageContent from './components/PageContent';
import ResponsiveShadow from './components/ResponsiveShadow';

function CatserImport() {
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState('');
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [catserList, setCatserList] = useState([]);
  const [importStats, setImportStats] = useState(null);
  const [importHistory, setImportHistory] = useState([]);
  const [user, setUser] = useState(null);
  const [usuarioBind, setUsuarioBind] = useState(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const token = localStorage.getItem('token');
  const navigate = useNavigate();

  useEffect(() => {
    // Validar usuário e permissões
    const userData = localStorage.getItem('user');
    const usuarioBindData = localStorage.getItem('usuarioBind');

    if (!userData || !usuarioBindData) {
      navigate('/');
      return;
    }

    const parsedUser = JSON.parse(userData);
    const parsedUsuarioBind = JSON.parse(usuarioBindData);

    setUser(parsedUser);
    setUsuarioBind(parsedUsuarioBind);

    // manter apenas validação mínima do usuário presente; botões fazem validação

    loadStats();
    fetchCatser();
    fetchImportHistory();
  }, [token, navigate]);

  const loadStats = async () => {
    try {
      const statsResponse = await axios.get(
        `${API_BASE_URL.CATSER}/estatisticas`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setImportStats(statsResponse.data);
    } catch (err) {
      console.error('Erro ao carregar estatísticas:', err);
    }
  };

  const fetchImportHistory = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL.CATSER}/import-log?tipoImportacao=CATSER`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setImportHistory(response.data || []);
    } catch (err) {
      setImportHistory([]);
    }
  };

  const fetchCatser = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL.CATSER}/all`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCatserList(response.data);
    } catch (err) {
      setError('Erro ao carregar dados de Catser');
      setShowErrorModal(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFullImport = async () => {
    if (!isSuperAdmin(user)) {
      setError('Acesso negado. Você não possui permissão para executar esta ação.');
      setShowErrorModal(true);
      return;
    }

    setIsLoading(true);
    setSuccess('');
    setProgress('Iniciando importação completa...');
    try {
      const response = await axios.post(
        `${API_BASE_URL.CATSER}/importar-todos`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 300000,
        }
      );
      if (response.data.success) {
        setSuccess(
          `Importação completa realizada com sucesso! ${
            response.data.totalSalvos || 0
          } registros salvos, ${response.data.totalDuplicados || 0} duplicados.`
        );
        loadStats();
        fetchCatser();
        fetchImportHistory();
      } else {
        setError(response.data.message || 'Erro desconhecido na importação');
        setShowErrorModal(true);
      }
    } catch (err) {
      console.error('Erro na importação completa:', err);
      if (err.code === 'ECONNABORTED') {
        setError(
          'Timeout na operação. A importação pode ainda estar em andamento.'
        );
      } else {
        setError(
          err.response?.data?.message || 'Erro ao executar importação completa'
        );
      }
      setShowErrorModal(true);
    } finally {
      setIsLoading(false);
      setProgress('');
    }
  };

  const refreshStats = () => {
    loadStats();
  };

  const formatDateTime = dateTime => {
    if (!dateTime) return '-';
    try {
      const date = typeof dateTime === 'string' ? new Date(dateTime) : dateTime;
      if (isNaN(date.getTime())) return 'Data inválida';
      return date.toLocaleString('pt-BR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    } catch {
      return 'Erro na data';
    }
  };

  const formatNumber = number => {
    if (typeof number !== 'number') return number;
    return number.toLocaleString('pt-BR');
  };

  if (!user && !showErrorModal) {
    return (
      <Layout>
        <div className="container-fluid">
          <div className="row">
            <div className="col-12">
              <div className="card shadow-sm">
                <div className="card-body text-center">
                  <div className="alert alert-danger" role="alert">
                    <h4 className="alert-heading">Acesso Negado</h4>
                    <p>
                      Você não possui permissão para acessar esta
                      funcionalidade.
                    </p>
                    <p className="mb-0">
                      Apenas administradores autorizados podem importar dados
                      CATSER.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <ResponsiveShadow>
        <PageHeader title="Importação CATSER" icon="bi-download" closeRoute="/home" backButton={false} />
        <PageContent style={{ paddingBottom: 0 }}>
          {isSuperAdmin(user) && (
            <Toolbar
              actions={[
                { key: 'import', label: isLoading ? 'Importando...' : 'Importar Dados', icon: 'fas fa-download', variant: 'primary', onClick: handleFullImport, disabled: isLoading },
                { key: 'refresh', label: 'Atualizar Estatísticas', icon: 'fas fa-refresh', variant: 'outline-info', onClick: refreshStats, disabled: isLoading }
              ]}
            />
          )}

          {/* Alertas e progresso */}
          {success && (
            <div className="alert alert-success alert-dismissible fade show" role="alert">
              <i className="fas fa-check-circle me-2"></i>
              {success}
              <button type="button" className="btn-close" onClick={() => setSuccess('')}></button>
            </div>
          )}
          {progress && (
            <div className="alert alert-info" role="alert">
              <i className="fas fa-spinner fa-spin me-2"></i>
              {progress}
            </div>
          )}

          {/* Histórico de Importações */}
          <TableContainer className="table-responsive mb-2">
        <h5 className="text-primary fw-bold mb-2">
          <i className="fas fa-history me-2"></i>
          Histórico de Importações
        </h5>
        <table className="table table-bordered table-sm table-custom-radius">
          <TableHeader columns={[
            { label: 'Data/Hora' },
            { label: 'Tipo' },
            { label: 'Total Importado' },
            { label: 'Usuário' },
            { label: 'Detalhes' },
          ]} />
          <tbody>
            {importHistory.length > 0 ? (
              importHistory.map((log, idx) => (
                <tr key={idx}>
                  <td>{formatDateTime(log.dataHora)}</td>
                  <td>{log.tipoImportacao}</td>
                  <td>{formatNumber(log.totalImportado)}</td>
                  <td>{log.usuario}</td>
                  <td>{log.detalhes}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" className="text-center text-muted">
                  Nenhuma importação realizada ainda
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </TableContainer>

      {/* Modal de Erro */}
      <DefaultModal
        show={showErrorModal}
        type="error"
        message={error}
        onClose={() => setShowErrorModal(false)}
      />
        </PageContent>
      </ResponsiveShadow>
    </Layout>
  );
}

export default CatserImport;
